import { FC, useMemo, useState } from 'react'
import {
	Box,
	Button,
	ColumnLayout,
	Container,
	ContentLayout,
	FormField,
	Header,
	Input,
	Select,
	SpaceBetween
} from '@cloudscape-design/components'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import useSingleCluster from '../../../../../hooks/query/useSingleCluster'
import useSingleTags from '../../../../../hooks/query/useSingleTags'
import useCreateMultiTenantRequest from '../../../../../hooks/mutate/useCreateMultiTenantRequest'
import LoadingSpinner from '../../../../../components/LoadingSpinner'
import type { TenantCidrAllocation } from '../../../../../types/multiTenantRequest'
import { getTagsApprovalObject } from '../../../../../types/tags'

const cidrRegex =
	/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\/([0-9]|[1-2][0-9]|3[0-2])$/

const NewMultiTenantRequest: FC = () => {
	const navigate = useNavigate()
	const { id: clusterId } = useParams() as { id: string }

	const { authState } = useOktaAuth()
	const currentUserEmail = (authState?.idToken?.claims?.email as string) ?? ''

	const { data: cluster, isLoading } = useSingleCluster(clusterId)

	const { data: tags } = useSingleTags(cluster?.tagsID ?? '', {
		enabled: !!cluster?.tagsID
	})

	const { mutate: createRequest, isLoading: isSubmitting } =
		useCreateMultiTenantRequest(clusterId)

	const environment = cluster?.details?.environment?.toLowerCase() ?? ''
	const isProductionEnv = environment === 'pd'

	const rawPlatformName =
		(tags?.details as Record<string, unknown>)?.platformName ??
		(tags?.details as Record<string, unknown>)?.tagPlatform ??
		(tags?.details as Record<string, unknown>)?.platform ??
		'-'

	const platformName =
		typeof rawPlatformName === 'object' &&
		rawPlatformName !== null &&
		'value' in rawPlatformName
			? String((rawPlatformName as { value: string }).value)
			: String(rawPlatformName ?? '-')

	const defaultCidrAllocations = useMemo<TenantCidrAllocation[]>(
		() => [
			{
				id: 'shared-workernode',
				purpose: 'Shared-Workernode',
				cidrAzA: '',
				cidrAzB: '',
				cidrAzC: isProductionEnv ? '' : undefined,
				isDefault: true
			},
			{
				id: 'workernode',
				purpose: 'Workernode',
				cidrAzA: '',
				cidrAzB: '',
				cidrAzC: isProductionEnv ? '' : undefined,
				isDefault: true
			},
			{
				id: 'pods',
				purpose: 'Pods',
				cidrAzA: isProductionEnv ? '100.64.0.0/18' : '100.64.0.0/17',
				cidrAzB: isProductionEnv ? '100.64.64.0/18' : '100.64.128.0/17',
				cidrAzC: isProductionEnv ? '100.64.128.0/18' : undefined,
				isDefault: true
			}
		],
		[isProductionEnv]
	)

	const [newSystemAlias, setNewSystemAlias] = useState('')
	const [tenantProjectName, setTenantProjectName] = useState('')
	const [wbsCode, setWbsCode] = useState('')
	const [costCenter, setCostCenter] = useState('')
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

	const [dataClassification, setDataClassification] = useState({
		label: 'PRIVATE',
		value: 'PRIVATE'
	})

	const [fundingSourceType, setFundingSourceType] = useState({
		label: 'CapEx',
		value: 'CapEx'
	})

	const [cidrAllocations, setCidrAllocations] =
		useState<TenantCidrAllocation[]>(defaultCidrAllocations)

	const updateCidrAllocation = (
		id: string,
		field: keyof TenantCidrAllocation,
		value: string
	) => {
		setCidrAllocations((current) =>
			current.map((item) =>
				item.id === id
					? {
							...item,
							[field]: field === 'purpose' ? value.toUpperCase() : value
					  }
					: item
			)
		)
	}

	const addCidrAllocation = () => {
		setCidrAllocations((current) => [
			...current,
			{
				id: `custom-${Date.now()}`,
				purpose: '',
				cidrAzA: '',
				cidrAzB: '',
				cidrAzC: isProductionEnv ? '' : undefined,
				isDefault: false
			}
		])
	}

	const removeCidrAllocation = (id: string) => {
		setCidrAllocations((current) => current.filter((item) => item.id !== id))
	}

	const hostedZoneName = newSystemAlias
		? isProductionEnv
			? `${newSystemAlias.toLowerCase()}.globetel.cloud`
			: `${newSystemAlias.toLowerCase()}-${environment}.globetel.cloud`
		: ''

	const validateSubmit = () => {
		const errors: Record<string, string> = {}

		if (!newSystemAlias.trim()) {
			errors.systemAlias = 'System Alias is required.'
		}

		if (!tenantProjectName.trim()) {
			errors.tenantProjectName = 'Tenant Project Name is required.'
		}

		if (!wbsCode.trim()) {
			errors.wbsCode = 'WBS Code is required.'
		}

		if (!costCenter.trim()) {
			errors.costCenter = 'Cost Center is required.'
		}

		cidrAllocations.forEach((allocation) => {
			if (allocation.id === 'pods') return

			if (!allocation.purpose.trim()) {
				errors[`${allocation.id}-purpose`] = 'Purpose is required.'
			}

			if (!allocation.cidrAzA.trim()) {
				errors[`${allocation.id}-cidrAzA`] = 'CIDR AZ-A is required.'
			} else if (!cidrRegex.test(allocation.cidrAzA)) {
				errors[`${allocation.id}-cidrAzA`] =
					'Invalid CIDR format. Example: 100.64.0.0/16'
			}

			if (!allocation.cidrAzB.trim()) {
				errors[`${allocation.id}-cidrAzB`] = 'CIDR AZ-B is required.'
			} else if (!cidrRegex.test(allocation.cidrAzB)) {
				errors[`${allocation.id}-cidrAzB`] =
					'Invalid CIDR format. Example: 100.64.0.0/16'
			}

			if (isProductionEnv && allocation.cidrAzC !== undefined) {
				if (!allocation.cidrAzC.trim()) {
					errors[`${allocation.id}-cidrAzC`] = 'CIDR AZ-C is required.'
				} else if (!cidrRegex.test(allocation.cidrAzC)) {
					errors[`${allocation.id}-cidrAzC`] =
						'Invalid CIDR format. Example: 100.64.0.0/16'
				}
			}
		})

		setValidationErrors(errors)
		return Object.keys(errors).length === 0
	}

	const handleSubmit = () => {
		if (!validateSubmit()) return

		createRequest(
			{
				action: 'CREATE',
				clusterId,
				requestorID: currentUserEmail,
				status: 'PENDING_APPROVAL',
				details: {
					accountId: cluster?.details?.accountID ?? '',
					region: 'ap-southeast-1',
					distributionList: cluster?.details?.distributionList ?? '',
					environment,
					workloadCluster:
						cluster?.details?.clusterName ?? cluster?.clusterID ?? '',
					platformName: String(platformName),
					portfolio: cluster?.details?.portfolio ?? '',
					systemAlias: newSystemAlias,
					tenantName: newSystemAlias,
					tenantProjectName,
					wbsCode,
					costCenter,
					dataClassification: dataClassification.value,
					fundingSourceType: fundingSourceType.value,
					cidrAllocations,
					hostedZoneName,
					dnsZone: hostedZoneName
				},
				approvalStatus: getTagsApprovalObject()
			},
			{
				onSuccess: () => {
					navigate(`/clusters/${clusterId}/multi-tenant`)
				}
			}
		)
	}

	if (isLoading) return <LoadingSpinner />

	return (
		<ContentLayout
			header={
				<Header
					variant="h1"
					description="Create a new multi-tenant onboarding request for this workload cluster."
				>
					New Multi-Tenant Request
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<Container header={<Header variant="h2">Request Form</Header>}>
					<ColumnLayout columns={3} variant="text-grid">
						<div>
							<Box variant="awsui-key-label">AWS Account ID</Box>
							<div>{cluster?.details?.accountID ?? '-'}</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Region</Box>
							<div>ap-southeast-1</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Distribution List</Box>
							<div>{cluster?.details?.distributionList ?? '-'}</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Environment</Box>
							<div>{cluster?.details?.environment ?? '-'}</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Requestor</Box>
							<div>{currentUserEmail || '-'}</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Workload Cluster Name</Box>
							<div>
								{cluster?.details?.clusterName ?? cluster?.clusterID ?? '-'}
							</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Platform Name</Box>
							<div>{platformName}</div>
						</div>

						<div>
							<Box variant="awsui-key-label">Portfolio</Box>
							<div>{cluster?.details?.portfolio ?? '-'}</div>
						</div>
					</ColumnLayout>
				</Container>

				<Container header={<Header variant="h2">Tenant Details</Header>}>
					<ColumnLayout columns={3}>
						<FormField
							label="New Tenant Name"
							errorText={validationErrors.systemAlias}
						>
							<Input
								value={newSystemAlias}
								onChange={({ detail }) =>
									setNewSystemAlias(detail.value.toUpperCase())
								}
							/>
						</FormField>

						<FormField
							label="Tenant Project Name"
							errorText={validationErrors.tenantProjectName}
						>
							<Input
								value={tenantProjectName}
								onChange={({ detail }) =>
									setTenantProjectName(detail.value.toUpperCase())
								}
							/>
						</FormField>

						<FormField label="WBS Code" errorText={validationErrors.wbsCode}>
							<Input
								value={wbsCode}
								onChange={({ detail }) => setWbsCode(detail.value.toUpperCase())}
							/>
						</FormField>

						<FormField
							label="Cost Center"
							errorText={validationErrors.costCenter}
						>
							<Input
								value={costCenter}
								placeholder="Type NA if None"
								onChange={({ detail }) =>
									setCostCenter(detail.value.toUpperCase())
								}
							/>
						</FormField>

						<FormField label="Data Classification">
							<Select
								selectedOption={dataClassification}
								onChange={({ detail }) =>
									setDataClassification({
										label: detail.selectedOption.label ?? '',
										value: detail.selectedOption.value ?? ''
									})
								}
								options={[
									{ label: 'PRIVATE', value: 'PRIVATE' },
									{ label: 'PUBLIC', value: 'PUBLIC' },
									{
										label: 'CONFIDENTIAL-PROPRIETARY',
										value: 'CONFIDENTIAL-PROPRIETARY'
									},
									{
										label: 'CONFIDENTIAL-PERSONAL',
										value: 'CONFIDENTIAL-PERSONAL'
									}
								]}
							/>
						</FormField>

						<FormField label="Funding Source Type">
							<Select
								selectedOption={fundingSourceType}
								onChange={({ detail }) =>
									setFundingSourceType({
										label: detail.selectedOption.label ?? '',
										value: detail.selectedOption.value ?? ''
									})
								}
								options={[
									{ label: 'CapEx', value: 'CapEx' },
									{ label: 'OpEx', value: 'OpEx' }
								]}
							/>
						</FormField>
					</ColumnLayout>
				</Container>

				<Container
					header={
						<Header
							variant="h2"
							description={
								isProductionEnv
									? 'Production workload clusters require CIDR allocations for AZ-A, AZ-B, and AZ-C.'
									: 'Non-production workload clusters require CIDR allocations for AZ-A and AZ-B.'
							}
						>
							CIDR Allocation / Subnet Tagging
						</Header>
					}
				>
					<SpaceBetween direction="vertical" size="m">
						{cidrAllocations.map((allocation) => (
							<ColumnLayout
								key={allocation.id}
								columns={isProductionEnv ? 5 : 4}
							>
								<FormField
									label="Purpose"
									errorText={validationErrors[`${allocation.id}-purpose`]}
								>
									<Input
										value={allocation.purpose}
										disabled={allocation.isDefault}
										placeholder="e.g. RDS"
										onChange={({ detail }) =>
											updateCidrAllocation(
												allocation.id,
												'purpose',
												detail.value
											)
										}
									/>
								</FormField>

								<FormField
									label="CIDR AZ-A"
									errorText={validationErrors[`${allocation.id}-cidrAzA`]}
								>
									<Input
										value={allocation.cidrAzA}
										placeholder="Example: 100.64.0.0/16"
										disabled={allocation.id === 'pods'}
										onChange={({ detail }) =>
											updateCidrAllocation(
												allocation.id,
												'cidrAzA',
												detail.value
											)
										}
									/>
								</FormField>

								<FormField
									label="CIDR AZ-B"
									errorText={validationErrors[`${allocation.id}-cidrAzB`]}
								>
									<Input
										value={allocation.cidrAzB}
										placeholder="Example: 100.64.0.0/16"
										disabled={allocation.id === 'pods'}
										onChange={({ detail }) =>
											updateCidrAllocation(
												allocation.id,
												'cidrAzB',
												detail.value
											)
										}
									/>
								</FormField>

								{isProductionEnv && (
									<FormField
										label="CIDR AZ-C"
										errorText={
											validationErrors[`${allocation.id}-cidrAzC`]
										}
									>
										<Input
											value={allocation.cidrAzC ?? ''}
											placeholder="Example: 100.64.0.0/16"
											disabled={allocation.id === 'pods'}
											onChange={({ detail }) =>
												updateCidrAllocation(
													allocation.id,
													'cidrAzC',
													detail.value
												)
											}
										/>
									</FormField>
								)}

								{!allocation.isDefault && (
									<Box padding={{ top: 'l' }}>
										<Button
											variant="normal"
											onClick={() => removeCidrAllocation(allocation.id)}
										>
											Remove
										</Button>
									</Box>
								)}
							</ColumnLayout>
						))}

						<Button onClick={addCidrAllocation}>+ Add Another Allocation</Button>
					</SpaceBetween>
				</Container>

				<Container header={<Header variant="h2">Hosted Zone</Header>}>
					<FormField
						label="Hosted Zone Name"
						description={
							isProductionEnv
								? 'For production, this is auto-generated using tenantName.globetel.cloud.'
								: 'For non-production, this is auto-generated using tenantName-env.globetel.cloud.'
						}
					>
						<Input
							value={hostedZoneName}
							disabled
							placeholder={
								isProductionEnv
									? 'tenant-name.globetel.cloud'
									: 'tenant-name-env.globetel.cloud'
							}
						/>
					</FormField>
				</Container>

				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button
							variant="link"
							onClick={() => navigate(`/clusters/${clusterId}`)}
						>
							Back
						</Button>

						<Button
							variant="primary"
							onClick={handleSubmit}
							loading={isSubmitting}
						>
							Submit
						</Button>
					</SpaceBetween>
				</Box>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default NewMultiTenantRequest
